import { createRef } from "react";
import { minutesToMs, msToMinutes } from ".";
import { GridSlot } from "../components/GridSlot";
import { getWeekOfMonth } from "./dates";
import { minutes, SlotKey, SlotNode, SlotTuple } from "./types";
import { useImmer } from "use-immer";

export function timespanOfMinutes(start: Date, slotSize: minutes) {
  return (_, i : number) => start.getTime() + minutesToMs(i * slotSize)
}

export function filterByHours(start: Date, end: Date) {
  return (time: number) => {
    const date = new Date(time);
    return date.getHours() >= start.getHours() && date.getHours() < end.getHours();
  }
}

export function filterByDayMinutes(start: Date, end: Date) {
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  return (time: number) => {
    const date = new Date(time);
    const dateMinutes = date.getHours() * 60 + date.getMinutes();
    return startMinutes <= dateMinutes && dateMinutes < endMinutes;
  }
}

export function filterByDay(start: Date, end: Date) {
  return (time: number) => {
    const date = new Date(time);
    return start.getDay() <= date.getDay() && date.getDay() <= end.getDay();
  }
}

export type SlotKeysGenerator = (
  start: Date, 
  end: Date, 
  options: {
    slotSize?: minutes;
    generator?: (start: Date, slotSize: minutes) => (key: number, i: number) => number;
    filter?: (start: Date, end: Date) => (time: number) => boolean;
  }
) => SlotKey[];

export const generateTimeslotKeys : SlotKeysGenerator = (
  start: Date, 
  end: Date, { 
    slotSize = 60,
    generator = timespanOfMinutes,
    filter = filterByDayMinutes
  }
) => {
  const span = msToMinutes(end.getTime() - start.getTime());
  const length = Math.ceil(span / slotSize);

  const keys = Array.from({ length },  generator(start, slotSize));
  const filteredKeys = keys.filter(filter(start, end));

  // TODO Not sure I like applying this default here.
  if (length === 0) {
    return [generator(start, slotSize)(0, 0)];
  } else {
    return filteredKeys
  }
}

export function groupSlotKeys(
  slotKeys: SlotKey[],
  groupBy: (key: number) => number,
) {
  // TODO: Replace with Object.group or Map.groupBy (when intellisense or whatever realizes it exists)
  return slotKeys.reduce((acc, key) => {
    const group = groupBy(key);
    if (!acc[group]) { acc[group] = []; }
    acc[group].push(key);
    return acc;
  }, {} as Record<number, number[]>);
}

export function generateGridSlots(
  slotKeys: SlotKey[],
  keyPrefix: string,
) : Record<SlotKey, SlotTuple> {
  return slotKeys.reduce((acc, key) => {
    const ref = createRef<HTMLDivElement | null>();
    const gridKey = `${keyPrefix}-${key}`;

    const Slot : SlotNode = ({children, className, ...props}) => 
      <GridSlot gridKey={gridKey} ref={ref} children={children} className={className} {...props} />

    acc[key] = { key, ref, Slot }
    return acc
  }, {} as Record<SlotKey, SlotTuple>);
}